import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Loader2, AlertCircle, X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOAuthConfig } from '../hooks/useOAuthConfig';
import { toast } from 'sonner';
import api, { roleSettingsApi } from '../services/api';
import { initSiteAnalytics } from '../utils/siteAnalytics';

// Microsoft Logo SVG component
const MicrosoftLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// Google Logo SVG component
const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, user } = useAuth();
  const { getOAuthUrl, isCustomOAuth } = useOAuthConfig();
  const [loading, setLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [enabledProviders, setEnabledProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);

  // Fetch enabled OAuth providers
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await api.get('/api/auth/providers');
        setEnabledProviders(response.data.providers || []);
      } catch (err) {
        console.error('Failed to fetch OAuth providers:', err);
        setEnabledProviders([{ provider: 'google', label: 'Google', icon: 'google' }]);
      } finally {
        setProvidersLoading(false);
      }
    };
    fetchProviders();
  }, []);

  // Check if already authenticated and redirect to role-based default
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      if (isAuthenticated && user) {
        try {
          const response = await roleSettingsApi.getDefaultRedirect();
          const targetUrl = response.data.default_url || '/products';
          navigate(targetUrl, { replace: true });
        } catch (err) {
          navigate('/products', { replace: true });
        }
      } else {
        setCheckingAuth(false);
      }
    };
    
    checkAuthAndRedirect();
  }, [isAuthenticated, user, navigate]);

  // Initialize site analytics for login page
  useEffect(() => {
    const cleanup = initSiteAnalytics();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Handle OAuth errors from redirect
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      if (errorParam === 'cancelled' || errorParam === 'access_denied') {
        setError('Sign in was cancelled');
      } else {
        setError(`Sign in failed: ${errorParam}`);
      }
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const handleGoogleLogin = () => {
    setLoadingProvider('google');
    const callbackPath = isCustomOAuth() ? '/auth/google/callback' : '/auth/callback';
    const redirectUrl = window.location.origin + callbackPath;
    
    const existingPricingStr = sessionStorage.getItem('signup_pricing');
    let pricingData = null;
    if (existingPricingStr && isCustomOAuth()) {
      try {
        pricingData = JSON.parse(existingPricingStr);
      } catch (e) {
        console.warn('Could not parse existing signup_pricing');
      }
    }
    
    const oauthUrl = getOAuthUrl(redirectUrl, pricingData);
    window.location.href = oauthUrl;
  };

  const handleMicrosoftLogin = () => {
    setLoadingProvider('microsoft');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const microsoftLoginUrl = `${backendUrl}/api/auth/microsoft/login`;
    window.location.href = microsoftLoginUrl;
  };

  const handleOAuthLogin = (provider) => {
    if (provider === 'google' || provider === 'emergent') {
      handleGoogleLogin();
    } else if (provider === 'microsoft') {
      handleMicrosoftLogin();
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/api/auth/login', { email, password });
      
      if (response.data.success) {
        login(response.data);
        toast.success('Login successful!');
        
        const pendingInviteToken = localStorage.getItem('pendingInviteToken');
        if (pendingInviteToken) {
          localStorage.removeItem('pendingInviteToken');
          navigate(`/collaboration/invite/${pendingInviteToken}`);
          return;
        }
        
        const returnUrl = sessionStorage.getItem('loginReturnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('loginReturnUrl');
          navigate(returnUrl);
          return;
        }
        
        try {
          const redirectResponse = await roleSettingsApi.getDefaultRedirect();
          const targetUrl = redirectResponse.data.default_url || '/products';
          navigate(targetUrl);
        } catch {
          if (response.data.role === 'admin') {
            navigate('/admin');
          } else {
            navigate('/products');
          }
        }
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background gradient effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-md w-full relative z-10">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div 
            className="inline-flex items-center gap-3 mb-6 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-xl font-semibold text-white tracking-tight">
              StackRanked.ai
            </span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
          <p className="text-gray-400">Sign in to continue to your account</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-8 shadow-2xl">
          
          {/* Error Banner */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-300 font-medium">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* OAuth Provider Buttons */}
          {providersLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {enabledProviders.map((provider) => (
                <Button
                  key={provider.provider}
                  onClick={() => handleOAuthLogin(provider.provider)}
                  disabled={loadingProvider !== null}
                  className="w-full bg-white hover:bg-gray-100 text-gray-900 font-medium py-6 flex items-center justify-center gap-3 transition-all"
                >
                  {loadingProvider === provider.provider ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : provider.icon === 'microsoft' ? (
                    <MicrosoftLogo />
                  ) : (
                    <GoogleLogo />
                  )}
                  {loadingProvider === provider.provider 
                    ? 'Signing in...' 
                    : `Continue with ${provider.label}`
                  }
                </Button>
              ))}
            </div>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0a0a0a] text-gray-500">or</span>
            </div>
          </div>

          {/* Email/Password Login */}
          {!showEmailLogin ? (
            <Button
              variant="outline"
              onClick={() => setShowEmailLogin(true)}
              className="w-full py-6 flex items-center justify-center gap-3 border-white/10 text-gray-300 hover:bg-white/5 hover:text-white"
            >
              <Mail className="w-5 h-5" />
              Continue with Email
            </Button>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                    }}
                    placeholder="you@example.com"
                    className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                    autoComplete="email"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <Link 
                    to="/forgot-password"
                    className="text-xs text-purple-400 hover:text-purple-300"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError(null);
                    }}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setShowEmailLogin(false);
                  setError(null);
                }}
                className="w-full text-sm text-gray-500 hover:text-gray-300"
              >
                Back to other options
              </button>
            </form>
          )}
        </div>

        {/* Sign Up Link */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">
            Don&apos;t have an account?{' '}
            <button 
              onClick={() => navigate('/signup')}
              className="text-purple-400 hover:text-purple-300 font-medium"
            >
              Sign up
            </button>
          </p>
        </div>
        
        {/* Legal Disclaimer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-600">
            By signing in, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
